/**
 * Modo claro / oscuro.
 *
 * Por defecto CLARO. El oscuro es una elección y se recuerda. No se mira
 * `prefers-color-scheme`: la tienda tiene un modo propio y manda la elección
 * del visitante. Decisión del cliente.
 *
 * El primer pintado no lo hace este archivo. Lo hace un script en línea en
 * `layout/theme.liquid`, dentro del `<head>` y sin `defer`, porque es la única
 * forma de que el atributo esté puesto ANTES de que el navegador pinte. Si
 * esperase a este módulo, la página se vería un fotograma con el modo
 * equivocado en cada recarga. Ese fogonazo es el error clásico de los
 * conmutadores de tema y solo se evita bloqueando el parser a propósito.
 *
 * Aquí solo vive lo que puede esperar: escuchar clics y mantener de acuerdo a
 * todos los interruptores de la página.
 */

const CLAVE = 'drikvar-tema';
const CLARO = 'claro';
const OSCURO = 'oscuro';
const raiz = document.documentElement;

/* El almacenamiento puede tirar excepción, no solo devolver vacío: en modo
   privado de algunos navegadores y con las cookies de terceros bloqueadas,
   leerlo lanza. Sin este try el interruptor entero se cae. */
function recordado() {
  try {
    return localStorage.getItem(CLAVE);
  } catch {
    return null;
  }
}

function recordar(modo) {
  try {
    localStorage.setItem(CLAVE, modo);
  } catch {
    /* Sin memoria el modo dura lo que la pestaña. Es peor, no es roto. */
  }
}

function modoActual() {
  return raiz.dataset.drikvarTema === CLARO ? CLARO : OSCURO;
}

/** Deja los interruptores diciendo la verdad. @param {string} modo */
function sincronizar(modo) {
  document.querySelectorAll('[data-drikvar-tema-toggle]').forEach((boton) => {
    boton.setAttribute('aria-checked', modo === CLARO ? 'true' : 'false');
  });

  /* El color del navegador en móvil. Sin esto, la barra de direcciones se
     queda oscura sobre una página clara y se ve un corte feo arriba. */
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', modo === CLARO ? '#e7e1d6' : '#0b0a0c');
}

/**
 * @param {string} modo
 * @param {boolean} [animar] - Falso en el arranque: nada que atenuar todavía.
 */
function aplicar(modo, animar) {
  if (animar) {
    raiz.classList.add('drikvar-tema-cambiando');
    /* 300ms = los 280 de la transición más un margen. Se quita porque dejarla
       puesta interpolaría colores en cada hover del sitio entero. */
    window.setTimeout(() => raiz.classList.remove('drikvar-tema-cambiando'), 300);
  }

  /* El atributo se ESCRIBE siempre, nunca se borra.

     Antes, con el oscuro de predeterminado, quitarlo era la forma correcta
     de volver a él: la hoja tiene el oscuro en `:root`. Ahora el
     predeterminado es el claro, así que borrarlo dejaría al visitante en
     oscuro justo cuando pide oscuro... y también al recargar sin haber
     elegido nada. `oscuro` no casa con ningún selector, y eso basta: cae en
     la base de la hoja, que es la que toca. */
  raiz.dataset.drikvarTema = modo === CLARO ? CLARO : OSCURO;

  sincronizar(modo);
}

function alternar() {
  const nuevo = modoActual() === CLARO ? OSCURO : CLARO;
  recordar(nuevo);
  aplicar(nuevo, true);
}

function montar() {
  /* El atributo ya lo puso el script en línea; esto solo pone al día los
     botones, que llegan con `aria-checked="false"` escrito en el HTML —y con
     el claro de predeterminado, casi siempre habrá que corregirlo. */
  sincronizar(modoActual());

  document.addEventListener('click', (evento) => {
    const boton = evento.target.closest('[data-drikvar-tema-toggle]');
    if (boton) alternar();
  });

  /* Otra pestaña del mismo sitio cambió el modo. Sin esto, dos pestañas
     abiertas se contradicen hasta que se recarga una. */
  window.addEventListener('storage', (evento) => {
    if (evento.key !== CLAVE) return;
    aplicar(evento.newValue === CLARO ? CLARO : OSCURO, true);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', montar);
} else {
  montar();
}

/* Para el cajón del carrito y el menú móvil, que se pintan después. */
window.DrikvarTema = { sincronizar: () => sincronizar(modoActual()) };
